import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useTranslation } from "react-i18next";
import StatCard from "./StatCard";
import { BRANCH_ANALYSIS_RADIUS } from "../../constants/branchAnalysis.styles";
import type { BranchOperationalRow } from "../../types/dashboard.type";

function googleMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

function safeNum(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return 0;
  return Number(value);
}

export interface BranchAnalysisCardProps {
  branch: BranchOperationalRow;
  /** When true, show as unassigned-waste bucket instead of a named branch. */
  isUnassigned?: boolean;
}

const BranchAnalysisCard: React.FC<BranchAnalysisCardProps> = ({
  branch,
  isUnassigned = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDark = theme.palette.mode === "dark";
  const lat = branch.latitude != null ? Number(branch.latitude) : NaN;
  const lng = branch.longitude != null ? Number(branch.longitude) : NaN;
  const hasLocation = Number.isFinite(lat) && Number.isFinite(lng);
  const displayName = isUnassigned
    ? t("branchAnalysis.unassignedBranch")
    : branch.name?.trim() || t("branchAnalysis.unnamedBranch");
  const totalWasteKg = safeNum(branch.total_waste_kg);
  const completedWasteKg = safeNum(branch.completed_waste_kg);
  const fillLevelReportCount = safeNum(branch.fill_level_report_count);
  const diversionRate = safeNum(branch.waste_diversion_rate_percent);

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: BRANCH_ANALYSIS_RADIUS,
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.45),
          boxShadow: isDark
            ? "none"
            : `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
          sx={{ mb: 1.5 }}
        >
          <Box minWidth={0} flex={1}>
            <Typography variant="subtitle1" fontWeight={700} noWrap title={displayName}>
              {displayName}
            </Typography>
            {!isUnassigned && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", wordBreak: "break-all", display: "block", mt: 0.25 }}
              >
                {branch.collection_site_id}
              </Typography>
            )}
          </Box>
          {branch.branch_contact && (
            <Chip
              size="small"
              icon={<PhoneOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              label={branch.branch_contact}
              variant="outlined"
              sx={{
                maxWidth: 160,
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          )}
        </Stack>

        {!isUnassigned && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2, minHeight: 24 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            {hasLocation ? (
              <Link
                href={googleMapsUrl(lat, lng)}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                fontWeight={600}
              >
                {t("branchAnalysis.openMap")}
              </Link>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t("branchAnalysis.locationNotSet")}
              </Typography>
            )}
          </Stack>
        )}

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <StatCard
              compact
              hideIcon
              hideTrend
              title={t("branchAnalysis.metrics.totalWaste")}
              value={totalWasteKg}
              unit="kg"
              trend={0}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              compact
              hideIcon
              hideTrend
              title={t("branchAnalysis.metrics.completedWaste")}
              value={completedWasteKg}
              unit="kg"
              trend={0}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              compact
              hideIcon
              hideTrend
              title={t("branchAnalysis.metrics.fillReports")}
              value={fillLevelReportCount}
              unit=""
              trend={0}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              compact
              hideIcon
              hideTrend
              title={t("branchAnalysis.metrics.diversionRate")}
              value={diversionRate}
              unit="%"
              trend={0}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BranchAnalysisCard;
